import { useCallback } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import {
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isAndroid } from 'shared/src/utils';

const SCROLL_THRESHOLD = 10;
const ANIMATION_DURATION = 300;
// Android: 손가락 대고 천천히 스크롤 시 짧은 momentum이 반복 발화되어 헤더가 깜빡거리는 현상 방지.
// 애니메이션 duration(300ms) + 안정 마진을 합친 500ms 동안 재호출 무시.
const ANDROID_TOGGLE_THROTTLE_MS = 500;

export const useScrollDirectionHeader = () => {
  const scrollY = useSharedValue(0);
  const prevScrollY = useSharedValue(0);
  const accumulatedDelta = useSharedValue(0);
  const measuredHeight = useSharedValue(0);
  const animatedHeight = useSharedValue(-1); // -1 = 미측정 상태 (auto-size)
  const isVisible = useSharedValue(true); // 현재 표시 상태 추적
  const lastAndroidEvaluationTime = useSharedValue(0); // Android throttle 기준 시각

  // iOS: 매 프레임 scrollY 변화 감지로 부드럽게 헤더 토글 (기존 동작)
  // Android: 매 프레임 reaction이 jank를 유발하므로 비활성. 대신 외부에서 스크롤 종료 시점에
  // evaluateDirectionAndroid를 호출하여 1회만 방향 판단하도록 분기.
  useAnimatedReaction(
    () => scrollY.value,
    (currentY, previousY) => {
      if (isAndroid) return;
      if (previousY === null || animatedHeight.value < 0) return;

      // 최상단이면 항상 노출
      if (currentY <= 0) {
        accumulatedDelta.value = 0;
        if (!isVisible.value) {
          isVisible.value = true;
          animatedHeight.value = withTiming(measuredHeight.value, {
            duration: ANIMATION_DURATION,
          });
        }
        prevScrollY.value = currentY;
        return;
      }

      const delta = currentY - prevScrollY.value;

      // 방향 전환 감지
      if (accumulatedDelta.value > 0 && delta < 0) {
        // 아래→위 전환: 숨김 상태면 즉시 노출
        accumulatedDelta.value = 0;
        if (!isVisible.value) {
          isVisible.value = true;
          animatedHeight.value = withTiming(measuredHeight.value, {
            duration: ANIMATION_DURATION,
          });
        }
      } else if (accumulatedDelta.value < 0 && delta > 0) {
        // 위→아래 전환: 누적값만 리셋
        accumulatedDelta.value = 0;
      }

      accumulatedDelta.value += delta;

      // 아래로 스크롤 임계값 도달 → 숨김
      if (accumulatedDelta.value > SCROLL_THRESHOLD && isVisible.value) {
        isVisible.value = false;
        animatedHeight.value = withTiming(0, {
          duration: ANIMATION_DURATION,
        });
      }

      prevScrollY.value = currentY;
    },
  );

  // Android 전용: 스크롤 종료(onMomentumEnd / onScrollEndDrag) 시점에 외부에서 호출.
  // 종료 직전 prevScrollY와 endY를 비교해 임계값 이상 차이 시 헤더 토글.
  // 매 프레임 처리하지 않아 jank 없음.
  const evaluateDirectionAndroid = useCallback((endY: number) => {
    'worklet';
    if (animatedHeight.value < 0) return;

    // throttle: 직전 평가 시각 기준 ANDROID_TOGGLE_THROTTLE_MS 이내면 무시.
    // 손가락 대고 천천히 움직일 때 onMomentumEnd / onScrollEndDrag가 반복 발화되어
    // 헤더가 위아래로 깜빡거리는 현상 방지.
    const now = Date.now();
    if (now - lastAndroidEvaluationTime.value < ANDROID_TOGGLE_THROTTLE_MS) {
      return;
    }
    lastAndroidEvaluationTime.value = now;

    if (endY <= 0) {
      if (!isVisible.value) {
        isVisible.value = true;
        animatedHeight.value = withTiming(measuredHeight.value, {
          duration: ANIMATION_DURATION,
        });
      }
      prevScrollY.value = endY;
      return;
    }

    const delta = endY - prevScrollY.value;
    if (delta > SCROLL_THRESHOLD && isVisible.value) {
      isVisible.value = false;
      animatedHeight.value = withTiming(0, {
        duration: ANIMATION_DURATION,
      });
    } else if (delta < -SCROLL_THRESHOLD && !isVisible.value) {
      isVisible.value = true;
      animatedHeight.value = withTiming(measuredHeight.value, {
        duration: ANIMATION_DURATION,
      });
    }

    prevScrollY.value = endY;
  }, [animatedHeight, isVisible, measuredHeight, prevScrollY, lastAndroidEvaluationTime]);

  // measuredHeight 변경 시 현재 보이는 상태면 즉시 동기화
  useAnimatedReaction(
    () => measuredHeight.value,
    (currentMeasured) => {
      if (isVisible.value && animatedHeight.value > 0) {
        animatedHeight.value = currentMeasured;
      }
    },
  );

  const collapsibleStyle = useAnimatedStyle(() => {
    // 측정 전에는 높이 제약 없이 auto-size (컨텐츠가 자연스럽게 렌더)
    if (animatedHeight.value < 0) {
      return {};
    }
    return {
      height: animatedHeight.value,
      overflow: 'hidden' as const,
    };
  });

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const height = event.nativeEvent.layout.height;
      // 부모가 height:0으로 축소되면 자식 layout도 0이 되므로, 0은 무시
      if (height > 0) {
        measuredHeight.value = height;
      }
      // 초기 측정 완료 → 현재 높이로 설정 (시각적 변화 없음)
      if (animatedHeight.value < 0 && height > 0) {
        animatedHeight.value = height;
      }
    },
    [measuredHeight, animatedHeight],
  );

  return { scrollY, collapsibleStyle, onLayout, evaluateDirectionAndroid };
};
